import { useEffect } from "react"
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx"
import LoadingAnimation from "../../components/LoadingAnimation"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx"

import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { NotificationTypes, addNotification } from "../../state/actions/notificationActions"

import { profileSignal, profileCcSchemaSignal, profileLoadedSignal, profileErrorSignal } from '../../state/signals/profileSignals'
import { profileSchemaSignal, profileSchemaVersionSignal, profileSchemaLoadedSignal, profileSchemaErrorSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import { checkApiInterfaceConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils.js'
import Loadable from "../../components/Loadable"
import { fetchUtg } from "../../state/actions/utgActions"


export default function ProfileApiInterfaceDetail() {

    const navigate = useNavigate()
    const { beId, profileId, id } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const [searchParams] = useSearchParams()
    const port = searchParams.get('port')
    const editMode = !!port
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken).then(
            () => {
                fetchSchemaManifest().then(
                    () => {
                        fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                    }
                )
            }
        )
    }, [beId, beIdChanged, profileId, config, accessToken])


    const apiInterfaceComputed = useComputed(() => {
        if (profileLoadedSignal.value && editMode) {
            console.log("Profile:")
            console.log(profileSignal.value)
            //const interfaces = profileSignal.value?.config?.apiInterfaceConfig?.apiInterface
            const index = apiInterfaceIndexComputed.value /*interfaces.findIndex(a => a.listenPort == port)*/
            if (index < 0) {
                throw Error("Invalid profile API interface index")
            }
            const currentApiInterface = profileSignal.value.config.apiInterfaceConfig.apiInterface[index]
            const jsonFormApiInterface = { config: currentApiInterface }
            return jsonFormApiInterface
        }
        return null
    })
    const apiInterfaceIndexComputed = useComputed(() => {
        if (profileLoadedSignal.value && editMode) {
            // selected apiInterface index. This is used when editing an interface since there are no unique id's
            const interfaces = profileSignal.value?.config?.apiInterfaceConfig?.apiInterface
            if (!interfaces) {
               return -1
            }
            const index = interfaces.findIndex(a => a.listenPort == port)
            return index
        }
        return -1
    })


    const apiInterfaceSchemaComputed = useComputed(() => {
        if (profileSchemaSignal.value?.properties) {
            // extract the selected api interface schema from the component config schema
            const jsonFormConfigSchema = { config: profileSchemaSignal.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items?.find(a => a.properties.cdType["s4-model-key"] == parseInt(id, 10)) }
            return jsonFormConfigSchema
        }
        return {}
    })
    const pageTitleComputed = useComputed(() => {
        const currentCaption = apiInterfaceSchemaComputed.value?.config?.properties?.cdType['s4-caption'] || ''
        return editMode ?
            `Manage ${currentCaption} UTG API Interface` :
            `Add ${currentCaption} UTG API Interface`
    })

    const jsonFormDataComputed = useComputed(() => {
        if (!profileSchemaLoadedSignal.value || !profileLoadedSignal.value) {
            return
        }
        if (isEmptyObject(profileSchemaSignal.value)) {
            //console.log("Profile schema is an empty object")
            return
        } else if (isEmptyObject(profileSignal.value)) {
            //console.log("Profile is an empty object")
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(apiInterfaceSchemaComputed.value)

        // disable editing of all fields if canEditSignal is false
        if (!canEditComputed.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }

        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'PROFILE'
        //console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        const formData = {
            schema: clonedSchema,
            loadedComponentConfig: profileSignal.value.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditComputed.value
        }
        if (apiInterfaceComputed.value) {
            formData.value = apiInterfaceComputed.value
        }
        //console.log('formData from ProfileApiInterfaceDetail', formData)
        return formData
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const isFormSubmittingSignal = useSignal(false)
    const accordionData = [
        { name: 'API Interface Data', data: apiInterfaceComputed.value },
        { name: 'API Interface Schema', data: apiInterfaceSchemaComputed.value },
        { name: 'API Interface Index', data: apiInterfaceIndexComputed.value },
        { name: 'Component Profile', data: profileSignal.value },
        { name: 'Profile Schema', data: profileSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmittingSignal.value) {
            return false
        }
        isFormSubmittingSignal.value = true
        try {
            //console.log('values', values)
            if (values) {

                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profileSignal.value.id,
                    name: profileSignal.value.name,
                    businessEntityId: profileSignal.value.businessEntityId,
                    config: profileSignal.value.config,
                }

                // make sure the apiInterfaceConfig is present before adding to it
                data.config.apiInterfaceConfig = data.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.apiInterfaceConfig.apiInterface[apiInterfaceIndexComputed.value] = values.config
                }
                else {
                    // insert values into the apiInterface array
                    data.config.apiInterfaceConfig.apiInterface.push(values.config)
                }

                // validate listenPort uniqueness in case jsonform validation fails
                if (checkApiInterfaceConflicts(data.config.apiInterfaceConfig.apiInterface)) {
                    addNotification("Unable to save due to conflict. All API Interfaces must have a unique Port.", NotificationTypes.ERROR)
                    return false
                }

                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileSchemaVersionSignal.value

                //console.log(data)
                updateProfile(beId, profileId, data, config, accessToken, true)
                    .then(
                        () => {
                            addNotification('Api Interface was successfully updated.', NotificationTypes.SUCCESS, true)
                        }
                    ).catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Api Interface.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmittingSignal.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        }
        else {
            navigate('../apiinterfaces')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    return (
        <>
            <ConfirmationModal
                show={showCancelModalSignal.value}
                handleCancel={
                    () => showCancelModalSignal.value = false
                }
                handleConfirm={
                    () => navigate('../apiinterfaces')
                }
                message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'}
                title={"Warning: Unsaved Changes"}
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    profileLoadedSignal.value ? (<>Manage {profileSignal.value.name}</>) : (<>Loading...</>)
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={pageTitleComputed.value} canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                    <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="2"></ProfileNav>
            <Loadable loadedSignals={[profileLoadedSignal, profileSchemaLoadedSignal]} errorSignals={[profileErrorSignal, profileSchemaErrorSignal]}>
                <JsonForm data={jsonFormDataComputed.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}